/**
 * Allys Projects List View
 * 
 * Allys-themed projects listing page component with filtering and sorting.
 * Uses AllysProjectCard for consistent styling.
 */

'use client';

// Allys Projects List View - Minimal elegant design
import type { PropertyDevelopmentProject } from '@/lib/api/clients/projects';
import { useCallback, useMemo, useState } from 'react';
import { AllysProjectCard } from './AllysProjectCard';

export interface AllysProjectsListViewProps {
  projects: PropertyDevelopmentProject[];
  organisationId: string;
  locale?: string;
  messages: {
    title: string;
    subtitle: string;
    filterAll: string;
    filterActive: string;
    filterUpcoming: string;
    filterCompleted: string;
    sortNewest: string;
    sortOldest: string;
    sortName: string;
    noProjects: string;
    noProjectsDescription: string;
    availableUnits: string;
    soldOut: string;
    comingSoon: string;
    enquireNow: string;
    viewProject: string;
    startingFrom: string;
    sortBy: string;
    filterBy: string;
    projects: string;
    searchPlaceholder?: string;
    filters?: string;
    status?: string;
    projectType?: string;
    priceRange?: string;
    units?: string;
    reset?: string;
    apply?: string;
    showingResults?: string;
    home?: string;
    developments?: string;
    project?: string;
    clearAllFilters?: string;
    exploreProject?: string;
    active?: string;
    total?: string;
  };
  className?: string;
}

type FilterType = 'ALL' | 'ACTIVE' | 'UPCOMING' | 'COMPLETED' | 'SOLD_OUT';
type SortType = 'newest' | 'oldest' | 'name';

/**
 * AllysProjectsListView - Main projects listing page component
 * 
 * Displays all property development projects with filtering and sorting.
 * Features a dramatic hero section and grid of project cards.
 */
export function AllysProjectsListView({
  projects,
  organisationId: _organisationId,
  locale = 'en',
  messages,
  className = '',
}: AllysProjectsListViewProps) {
  // State
  const [filter, setFilter] = useState<FilterType>('ALL');
  const [sort, setSort] = useState<SortType>('newest');
  const [searchValue, setSearchValue] = useState('');
  const [selectedTypes, setSelectedTypes] = useState<string[]>([]);
  // Price and units range filters - setters reserved for future filter UI implementation
  const [selectedPriceRange] = useState<[number, number]>([0, 10000000]);
  const [selectedUnitsRange] = useState<[number, number]>([0, 500]);
  
  
  // Search function
  const searchFn = useCallback((project: PropertyDevelopmentProject, query: string): boolean => {
    const lowerQuery = query.toLowerCase();
    return !!(
      project.name.toLowerCase().includes(lowerQuery) ||
      project.description?.toLowerCase().includes(lowerQuery) ||
      project.slug?.toLowerCase().includes(lowerQuery)
    );
  }, []);

  // Filter and sort projects
  const filteredAndSortedProjects = useMemo(() => {
    let filtered = [...projects];
    
    // Apply search
    if (searchValue) {
      filtered = filtered.filter(project => searchFn(project, searchValue));
    }

    // Apply status filter
    if (filter !== 'ALL') {
      filtered = filtered.filter(project => {
        if (filter === 'COMPLETED') {
          return project.status === 'COMPLETED_ARCHIVED' || project.actualCompletionDate;
        }
        return project.status === filter;
      });
    }
    
    // Apply type filter
    if (selectedTypes.length > 0) {
      filtered = filtered.filter(project => 
        project.projectType && selectedTypes.includes(project.projectType)
      );
    }
    
    // Apply price range filter (Note: PropertyDevelopmentProject doesn't have unit price fields yet)
    // This filter is ready for when the backend provides this data
    if (selectedPriceRange[0] > 0 || selectedPriceRange[1] < 10000000) {
      // filtered = filtered.filter(project => {
      //   const projectMinPrice = project.minimumUnitPrice || 0;
      //   const projectMaxPrice = project.maximumUnitPrice || Infinity;
      //   return projectMinPrice >= selectedPriceRange[0] && projectMaxPrice <= selectedPriceRange[1];
      // });
    }
    
    // Apply units range filter
    if (selectedUnitsRange[0] > 0 || selectedUnitsRange[1] < 500) {
      filtered = filtered.filter(project => {
        const totalUnits = project.totalUnits || 0;
        return totalUnits >= selectedUnitsRange[0] && totalUnits <= selectedUnitsRange[1];
      });
    }

    // Apply sort
      filtered.sort((a, b) => {
      switch (sort) {
        case 'newest':
          return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
        case 'oldest':
          return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
        case 'name':
          return a.name.localeCompare(b.name);
        default:
          return 0;
      }
    });

    return filtered;
  }, [projects, filter, sort, searchValue, selectedTypes, selectedPriceRange, selectedUnitsRange, searchFn]);

  // Count projects by status
  const statusCounts = useMemo(() => {
    return {
      ALL: projects.length,
      ACTIVE: projects.filter(p => p.status === 'ACTIVE').length,
      UPCOMING: projects.filter(p => p.status === 'UPCOMING').length,
      COMPLETED: projects.filter(p => p.status === 'COMPLETED_ARCHIVED' || p.actualCompletionDate).length,
      SOLD_OUT: projects.filter(p => p.status === 'SOLD_OUT').length,
    };
  }, [projects]);

  return (
    <div 
      className={`min-h-screen bg-gray-50 page-content font-inter ${className}`}
    >
      {/* Minimal Page Header - Clean, editorial style */}
      <div className="relative bg-white border-b border-gray-100">
        {/* Subtle accent line at top */}
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-amber-400/60 to-transparent" />
        
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-2 sm:gap-3 py-3 sm:py-6">
            {/* Left: Breadcrumb & Title */}
            <div className="flex-1 min-w-0">
              {/* Minimal breadcrumb */}
              <nav className="mb-1 flex items-center gap-1.5 text-xs text-gray-400">
                <a href={`/${locale}`} className="hover:text-gray-600 transition-colors">{messages.home || 'Home'}</a>
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
                <span className="text-gray-600">{messages.developments || 'Developments'}</span>
              </nav>
              
              {/* Title - Clean and professional */}
              <h1 className="text-xl sm:text-2xl lg:text-3xl font-semibold text-gray-900 tracking-tight">
                {messages.title}
              </h1>
            </div>
            
            {/* Right: Compact Stats */}
            <div className="flex items-center gap-2 flex-wrap sm:flex-nowrap">
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-gray-50 rounded-full text-xs text-gray-600 border border-gray-100">
                <span className="font-semibold text-gray-900">{projects.length}</span>
                {messages.total || 'Total'}
              </span>
              {statusCounts.UPCOMING > 0 && (
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-amber-50 rounded-full text-xs text-amber-700 border border-amber-100">
                  <span className="font-semibold">{statusCounts.UPCOMING}</span>
                  {messages.comingSoon || 'Coming Soon'}
                </span>
              )}
              {statusCounts.ACTIVE > 0 && (
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-emerald-50 rounded-full text-xs text-emerald-700 border border-emerald-100">
                  <span className="font-semibold">{statusCounts.ACTIVE}</span>
                  {messages.active || 'Active'}
                </span>
              )}
            </div>
          </div>
        </div>
      </div>
      
      {/* Unified Search & Filter Toolbar */}
      <div className="sm:sticky sm:top-[var(--nav-offset-top,0)] z-40 bg-white border-b border-gray-200 shadow-sm">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          {/* Main Toolbar Row */}
          <div className="flex items-center gap-2 sm:gap-3 py-2 sm:py-3">
            {/* Search Input - Compact */}
            <div className="relative flex-1 max-w-md">
              <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                <svg className="h-4 w-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
              <input
                type="text"
                placeholder={messages.searchPlaceholder || 'Search projects...'}
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
                className="block w-full rounded-lg border-0 bg-gray-100 py-2 pl-9 pr-3 text-sm text-gray-900 placeholder:text-gray-500 focus:bg-white focus:ring-2 focus:ring-amber-500/20 focus:outline-none transition-all"
              />
              {searchValue && (
                <button
                  onClick={() => setSearchValue('')}
                  className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 hover:text-gray-600"
                >
                  <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              )}
            </div>

            {/* Divider */}
            <div className="hidden sm:block h-6 w-px bg-gray-200" />

            {/* Status Filter Pills - Inline on Desktop */}
            <div className="hidden lg:flex items-center gap-1.5">
              {[
                { id: 'ALL', label: messages.filterAll, count: statusCounts.ALL },
                { id: 'ACTIVE', label: messages.filterActive, count: statusCounts.ACTIVE },
                { id: 'UPCOMING', label: messages.filterUpcoming, count: statusCounts.UPCOMING },
              ].map((status) => (
                <button
                  key={status.id}
                  onClick={() => setFilter(status.id as FilterType)}
                  className={`
                    inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all
                    ${filter === status.id 
                      ? 'bg-slate-900 text-white shadow-sm' 
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }
                  `}
                >
                  {status.label}
                  {status.count > 0 && (
                    <span className={`
                      px-1.5 py-0.5 rounded-full text-[10px] font-semibold
                      ${filter === status.id ? 'bg-white/20 text-white' : 'bg-gray-200 text-gray-500'}
                    `}>
                      {status.count}
                    </span>
                  )}
                </button>
              ))}
            </div>

            {/* Divider */}
            <div className="hidden lg:block h-6 w-px bg-gray-200" />

            {/* Sort Dropdown - Compact */}
            <div className="hidden sm:flex items-center gap-2">
              <span className="text-xs text-gray-500">{messages.sortBy || 'Sort:'}</span>
              <select
                value={sort}
                onChange={(e) => setSort(e.target.value as SortType)}
                className="appearance-none bg-transparent text-sm font-medium text-gray-700 cursor-pointer focus:outline-none pr-6"
                style={{ 
                  backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%236b7280'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E")`,
                  backgroundRepeat: 'no-repeat',
                  backgroundPosition: 'right 0 center',
                  backgroundSize: '16px'
                }}
              >
                <option value="newest">{messages.sortNewest}</option>
                <option value="oldest">{messages.sortOldest}</option>
                <option value="name">{messages.sortName}</option>
              </select>
            </div>

            {/* Results Count */}
            <div className="hidden md:flex items-center gap-2 text-sm text-gray-500 ml-auto">
              <span className="font-semibold text-gray-900">{filteredAndSortedProjects.length}</span>
              <span>{filteredAndSortedProjects.length === 1 ? (messages.project || 'project') : (messages.projects || 'projects')}</span>
            </div>

            {/* Mobile Filter Button */}
            <button
              onClick={() => {
                // Toggle mobile filter panel
                const panel = document.getElementById('mobile-filter-panel');
                if (panel) {
                  panel.classList.toggle('hidden');
                }
              }}
              className="lg:hidden inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-gray-100 text-sm font-medium text-gray-700 hover:bg-gray-200 transition-colors"
            >
              <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
              </svg>
              <span className="hidden sm:inline">{messages.filters || 'Filters'}</span>
              {(filter !== 'ALL' || selectedTypes.length > 0) && (
                <span className="flex h-4 w-4 items-center justify-center rounded-full bg-amber-500 text-[10px] font-bold text-white">
                  {(filter !== 'ALL' ? 1 : 0) + selectedTypes.length}
                </span>
              )}
            </button>
      </div>
      
          {/* Mobile Filter Panel - Hidden by default */}
          <div id="mobile-filter-panel" className="hidden lg:hidden border-t border-gray-100 py-3 space-y-3">
            {/* Status Pills for Mobile */}
            <div className="flex flex-wrap gap-2">
              {[
          { id: 'ALL', label: messages.filterAll, count: statusCounts.ALL },
          { id: 'ACTIVE', label: messages.filterActive, count: statusCounts.ACTIVE },
          { id: 'UPCOMING', label: messages.filterUpcoming, count: statusCounts.UPCOMING },
          { id: 'COMPLETED', label: messages.filterCompleted, count: statusCounts.COMPLETED },
              ].map((status) => (
                <button
                  key={status.id}
                  onClick={() => setFilter(status.id as FilterType)}
                  className={`
                    inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all
                    ${filter === status.id 
                      ? 'bg-slate-900 text-white' 
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }
                  `}
                >
                  {status.label}
                  <span className={`text-[10px] ${filter === status.id ? 'text-white/70' : 'text-gray-400'}`}>
                    ({status.count})
                  </span>
                </button>
              ))}
            </div>
            
            {/* Sort for Mobile */}
            <div className="flex items-center gap-2 sm:hidden">
              <span className="text-xs text-gray-500">{messages.sortBy || 'Sort by:'}</span>
              <select
                value={sort}
                onChange={(e) => setSort(e.target.value as SortType)}
                className="flex-1 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm focus:ring-2 focus:ring-amber-500/20 focus:outline-none"
              >
                <option value="newest">{messages.sortNewest}</option>
                <option value="oldest">{messages.sortOldest}</option>
                <option value="name">{messages.sortName}</option>
              </select>
            </div>

            {/* Reset Button */}
            {(filter !== 'ALL' || selectedTypes.length > 0 || searchValue) && (
              <button
                onClick={() => {
          setFilter('ALL');
          setSelectedTypes([]);
          setSearchValue('');
        }}
                className="text-xs text-amber-600 hover:text-amber-700 font-medium"
              >
                {messages.clearAllFilters || 'Clear all filters'}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Projects Grid - Magazine Editorial Layout */}
      <div className="bg-gradient-to-b from-gray-50 via-gray-100 to-gray-50 py-12 lg:py-16">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          {filteredAndSortedProjects.length > 0 ? (
            <div className="space-y-8 lg:space-y-12">
              {filteredAndSortedProjects.map((project, index) => {
                // First project is featured (full width, larger)
                const isFeatured = index === 0;
                // Alternate between layouts for visual interest
                const variant = isFeatured ? 'featured' : (index % 3 === 0 ? 'default' : 'default');
                
                return (
                  <div 
                    key={project.id}
                    className={`
                      ${isFeatured 
                        ? 'col-span-full' 
                        : index % 3 === 1 
                          ? 'lg:col-span-1' 
                          : 'lg:col-span-1'
                      }
                    `}
                    style={{
                      display: 'grid',
                      gridTemplateColumns: isFeatured ? '1fr' : undefined,
                    }}
                  >
                <AllysProjectCard
                  project={project}
                  href={`/${locale}/projects/${project.slug || project.id}`}
                  messages={{
                    availableUnits: messages.availableUnits,
                    soldOut: messages.soldOut,
                    comingSoon: messages.comingSoon,
                    enquireNow: messages.enquireNow,
                    viewProject: messages.viewProject,
                    startingFrom: messages.startingFrom,
                        explore: messages.exploreProject || 'Explore Project',
                  }}
                  locale={locale}
                      variant={variant}
                />
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="py-20 text-center">
              <div className="mx-auto mb-4 flex h-20 w-20 items-center justify-center rounded-full bg-gray-200">
                <svg className="h-10 w-10 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                </svg>
              </div>
              <h3 className="mb-2 text-xl font-bold text-gray-900">{messages.noProjects}</h3>
              <p className="text-gray-600">{messages.noProjectsDescription}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
